import React, { useState, useEffect, useRef, Fragment } from "react";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";

import {
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { HiPlus, HiArrowPath, HiAdjustmentsHorizontal } from "react-icons/hi2";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import { userService } from "../services/userService";
import {
  ActivityType,
  ActivityVariableDefinition,
  GlobalActivityCategory,
  GlobalActivityType,
} from "../types/activity";
import {
  getUnitsForVariable,
  getDefaultUnit,
  normalizeActivityVariables,
} from "../utils/unitUtils";
import {
  ActivityVariablePref,
  applyPreferenceToVariables,
  getActivityPrefKey,
} from "../utils/activityVariablePreferences";
import { useNavigate } from "react-router-dom";
import PeakTextField from "../components/onboarding/PeakTextField";
import BottomSheet from "../components/goals/BottomSheet";
import {
  LIST_ROW_CLASS,
  ListAddButton,
  ListFilterToggle,
  ListPagination,
  PeakSelect,
} from "../components/common/ListControls";
const Activities = () => {
  const navigate = useNavigate();
  const { currentUser, userData, authState, refreshUserData } = useAuth();
  const isImperial = authState.user?.isImperial ?? false;
  const [activities, setActivities] = useState<ActivityType[]>([]);

  const [showAddActivityForm, setShowAddActivityForm] = useState(false);

  const [activitiesSearchTerm, setActivitiesSearchTerm] = useState<string>("");
  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [showActivityFilters, setShowActivityFilters] = useState(false);
  const [detailActivity, setDetailActivity] = useState<ActivityType | null>(
    null,
  );

  const [activitiesPage, setActivitiesPage] = useState(1);
  const ACTIVITIES_PER_PAGE = 10;

  const [loadingActivities, setLoadingActivities] = useState<boolean>(false);

  // Global Activity Packs state
  const [globalCategories, setGlobalCategories] = useState<
    GlobalActivityCategory[]
  >([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, GlobalActivityType[]>
  >({});
  const [packActivityTypes, setPackActivityTypes] = useState<ActivityType[]>(
    [],
  );
  const [loadingGlobalActivities, setLoadingGlobalActivities] =
    useState<boolean>(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [packsSearchTerm, setPacksSearchTerm] = useState<string>("");
  const [userAddedPackIds, setUserAddedPackIds] = useState<string[]>([]);
  const [togglingPackId, setTogglingPackId] = useState<string | null>(null);

  // Activity type management state
  const [editingActivityType, setEditingActivityType] =
    useState<ActivityType | null>(null);
  const [activityTypeError, setActivityTypeError] = useState<string | null>(
    null,
  );
  const [savingActivityTypes, setSavingActivityTypes] =
    useState<boolean>(false);
  const [activityTypeToDelete, setActivityTypeToDelete] =
    useState<ActivityType | null>(null);

  // Names of variables the athlete has hidden from display (saved as a preference)
  const [hiddenVars, setHiddenVars] = useState<string[]>([]);

  const [activityFormData, setActivityFormData] = useState<{
    name: string;
    description: string;
    variables: ActivityVariableDefinition[];
    image?: string;
  }>({ name: "", description: "", variables: [], image: "" });

  const [customVariableName, setCustomVariableName] = useState<string>("");
  const [customVariableUnit, setCustomVariableUnit] = useState<string>("units");
  const [showCustomVariableInput, setShowCustomVariableInput] =
    useState<boolean>(false);
  const [showActivityResetConfirmation, setShowActivityResetConfirmation] =
    useState<boolean>(false);

  const customVariableInputRef = useRef<HTMLDivElement>(null);

  const AVAILABLE_VARIABLES: string[] = [
    "Distance",
    "Incline",
    "Intensity",
    "Reps",
    "Rest",
    "Speed",
    "Time",
    "Weight",
  ];

  // Exercise Library & custom exercise creation are available on every tier.
  const canCreateActivity = true;

  // Library (pack) exercises can't have their definition edited — only their
  // display (order / units / hidden) can be customized by the athlete.
  const editingIsFromPack = Boolean(editingActivityType?.isFromPack);

  // Fetch activity types from Firebase
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      try {
        setLoadingActivities(true);
        const typesData = await activityService.fetchActivityTypes(
          currentUser.uid,
        );
        setActivities(typesData);
      } catch (error) {
        console.error("Error fetching activities:", error);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchData();
  }, [currentUser?.uid]);

  // Load user's added pack IDs from userData and fetch pack activity types
useEffect(() => {

  if (userData?.adminActivityCatIDs) {
    setUserAddedPackIds(userData.adminActivityCatIDs);
    const packIds = userData.adminActivityCatIDs;

    const fetchPackActivities = async () => {
      if (packIds.length === 0) {
        setPackActivityTypes([]);
        return;
      }
      try {
        const globalTypes =
          await GlobalActivityService.fetchActivityTypesForCategories(packIds);

        const converted: ActivityType[] = globalTypes.map(
          (gt: GlobalActivityType) => ({
            id: gt.id,
            name: gt.name,
            description: gt.description,
            variables: gt.variables.map((v): ActivityVariableDefinition => {
              if (typeof v === "string") {
                return {
                  name: v,
                  defaultUnit: getDefaultUnit(v, isImperial),
                };
              }
              return {
                ...v,
                defaultUnit: getDefaultUnit(v.name, isImperial),
              };
            }),
            image: gt.image,
            isFromPack: true,
            categoryId: gt.categoryId,
          }),
        );
        setPackActivityTypes(converted);
      } catch (error) {
        console.error("[PackDebug] Error fetching pack activity types:", error);
      }
    };
    fetchPackActivities();
  } else {
    setUserAddedPackIds([]);
    setPackActivityTypes([]);
  }
}, [userData?.adminActivityCatIDs, isImperial]);
  // Fetch global activity packs
  useEffect(() => {
    const fetchGlobalActivities = async () => {
      if (globalCategories.length > 0) return;
      // if (userAddedPackIds.length === 0) return;
      try {
        setLoadingGlobalActivities(true);
        const categories = await GlobalActivityService.fetchCategories();
        setGlobalCategories(categories);
      } catch (error) {
        console.error("Error fetching global activity categories:", error);
      } finally {
        setLoadingGlobalActivities(false);
      }
    };
    fetchGlobalActivities();
  }, [globalCategories.length, userAddedPackIds.length]);

  // Fetch activity types for all categories to show counts
  useEffect(() => {
    const fetchAllCategoryActivityTypes = async () => {
      if (globalCategories.length === 0) return;
      const categoriesToFetch = globalCategories.filter(
        (category) => category.id && !globalActivityTypes[category.id],
      );
      if (categoriesToFetch.length === 0) return;
      try {
        const fetchPromises = categoriesToFetch.map((category) =>
          GlobalActivityService.fetchActivityTypes(category.id!).then(
            (types) => ({
              categoryId: category.id!,
              types,
            }),
          ),
        );
        const results = await Promise.all(fetchPromises);
        setGlobalActivityTypes((prev) => {
          const updated = { ...prev };
          results.forEach(({ categoryId, types }) => {
            updated[categoryId] = types;
          });
          return updated;
        });
      } catch (error) {
        console.error("Error fetching activity types for categories:", error);
      }
    };
    fetchAllCategoryActivityTypes();
  }, [globalCategories]);

  // Toggle a pack (category) in user's library
  const handleTogglePack = async (categoryId: string) => {
    if (!currentUser?.uid) return;
    try {
      setTogglingPackId(categoryId);
      const isCurrentlyAdded = userAddedPackIds.includes(categoryId);
      let updatedPackIds: string[];
      if (isCurrentlyAdded) {
        updatedPackIds = userAddedPackIds.filter((id) => id !== categoryId);
      } else {
        updatedPackIds = [...userAddedPackIds, categoryId];
      }
      await userService.updateUserProfile(currentUser.uid, {
        adminActivityCatIDs: updatedPackIds,
      });
      setUserAddedPackIds(updatedPackIds);
      await refreshUserData();
    } catch (error) {
      console.error("Error toggling activity pack:", error);
      setActivityTypeError(
        error instanceof Error
          ? error.message
          : "Failed to update your library. Please try again.",
      );
    } finally {
      setTogglingPackId(null);
    }
  };

  // Fetch activity types when a category is expanded
  const fetchCategoryActivityTypes = async (categoryId: string) => {
    if (globalActivityTypes[categoryId]) return;
    try {
      const types = await GlobalActivityService.fetchActivityTypes(categoryId);
      setGlobalActivityTypes((prev) => ({ ...prev, [categoryId]: types }));
    } catch (error) {
      console.error("Error fetching activity types for category:", error);
    }
  };

  // Reset pagination when search terms change
  useEffect(() => {
    setActivitiesPage(1);
  }, [activitiesSearchTerm]);

  // Activity form handlers
  const resetActivityTypeForm = () => {
    setActivityFormData({
      name: "",
      description: "",
      variables: [],
      image: "",
    });
    setActivityTypeError(null);
    setShowAddActivityForm(false);
    setEditingActivityType(null);
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setHiddenVars([]);
  };

  const toggleVariable = (variableName: string, unit?: string) => {
    setActivityFormData((prev) => {
      const existingIndex = prev.variables.findIndex(
        (v) => v.name === variableName,
      );
      if (existingIndex !== -1) {
        // Removing the variable also clears any hidden flag for it
        setHiddenVars((h) => h.filter((n) => n !== variableName));
        return {
          ...prev,
          variables: prev.variables.filter((_, idx) => idx !== existingIndex),
        };
      } else {
        const defaultUnit = unit || getDefaultUnit(variableName, isImperial);
        return {
          ...prev,
          variables: [...prev.variables, { name: variableName, defaultUnit }],
        };
      }
    });
    setActivityTypeError(null);
  };

  // Reorder a selected variable (defines the default display order)
  const moveVariable = (index: number, direction: "up" | "down") => {
    setActivityFormData((prev) => {
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.variables.length) return prev;
      const next = [...prev.variables];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return { ...prev, variables: next };
    });
  };

  // Toggle whether a variable is hidden from display by default
  const toggleHiddenVariable = (variableName: string) => {
    setHiddenVars((prev) =>
      prev.includes(variableName)
        ? prev.filter((n) => n !== variableName)
        : [...prev, variableName],
    );
  };

  const updateVariableUnit = (variableName: string, newUnit: string) => {
    setActivityFormData((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.name === variableName ? { ...v, defaultUnit: newUnit } : v,
      ),
    }));
  };

  const isVariableSelected = (variableName: string): boolean => {
    return activityFormData.variables.some((v) => v.name === variableName);
  };

  const addCustomVariable = () => {
    const trimmedName = customVariableName.trim();
    if (!trimmedName) {
      setActivityTypeError("Please enter a variable name");
      return;
    }
    const existingVariableNames = activityFormData.variables.map((v) => v.name);
    const normalizedName = trimmedName.toLowerCase();
    const presetVariables = AVAILABLE_VARIABLES.map((v) => v.toLowerCase());
    if (
      existingVariableNames.some((v) => v.toLowerCase() === normalizedName) ||
      presetVariables.includes(normalizedName)
    ) {
      setActivityTypeError("This variable already exists");
      return;
    }
    toggleVariable(trimmedName, customVariableUnit);
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setActivityTypeError(null);
  };

  const cancelCustomVariable = () => {
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setActivityTypeError(null);
  };

  // Handle clicks outside custom variable input to cancel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCustomVariableInput &&
        customVariableInputRef.current &&
        !customVariableInputRef.current.contains(event.target as Node)
      ) {
        cancelCustomVariable();
      }
    };
    if (showCustomVariableInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomVariableInput]);

  const handleActivitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.uid) return;
    if (!activityFormData.name.trim()) {
      setActivityTypeError("Exercise name is required");
      return;
    }
    if (activityFormData.variables.length === 0) {
      setActivityTypeError("Please select at least one variable");
      return;
    }
    try {
      setSavingActivityTypes(true);
      setActivityTypeError(null);

      const isFromPack = Boolean(editingActivityType?.isFromPack);

      // 1. Persist the exercise definition (order + units).
      //    Library (pack) exercises can't be edited, so we only save the
      //    athlete's display preference for those.
      let activityId = editingActivityType?.id;
      if (!isFromPack) {
        if (activityId) {
          await activityService.updateActivityType(
            currentUser.uid,
            activityId,
            {
              name: activityFormData.name,
              description: activityFormData.description,
              variables: activityFormData.variables,
              image: activityFormData.image,
              createdAt: editingActivityType?.createdAt,
            },
          );
        } else {
          activityId = await activityService.createActivityType(
            currentUser.uid,
            {
              name: activityFormData.name,
              description: activityFormData.description,
              variables: activityFormData.variables,
              image: activityFormData.image,
            },
          );
        }
      }

      // 2. Persist (or clear) the athlete's display preference for this exercise.
      await persistVariablePreference(activityId, isFromPack);

      const typesData = await activityService.fetchActivityTypes(
        currentUser.uid,
      );
      setActivities(typesData);
      resetActivityTypeForm();
    } catch (error) {
      console.error("Error saving activity type:", error);
      setActivityTypeError(
        error instanceof Error
          ? error.message
          : "Failed to save exercise. Please try again.",
      );
    } finally {
      setSavingActivityTypes(false);
    }
  };

  // Save the display preference (order/unit/hidden) derived from the form, or
  // clear it when the arrangement matches the exercise's defaults.
  const persistVariablePreference = async (
    activityId: string | undefined,
    isFromPack: boolean,
  ) => {
    if (!currentUser?.uid || !activityId) return;

    const key = getActivityPrefKey({
      activityTypeId: activityId,
      globalCategoryId: isFromPack ? editingActivityType?.categoryId : undefined,
    });

    // Default arrangement = definition order, default units, nothing hidden.
    const baseVariables = normalizeActivityVariables(
      editingActivityType?.variables || activityFormData.variables,
    );
    const matchesDefault =
      hiddenVars.length === 0 &&
      baseVariables.length === activityFormData.variables.length &&
      activityFormData.variables.every(
        (v, i) =>
          baseVariables[i]?.name === v.name &&
          baseVariables[i]?.defaultUnit === v.defaultUnit,
      );

    const existingPrefs = userData?.activityVariablePreferences || {};
    const updatedPrefs = { ...existingPrefs };

    if (matchesDefault) {
      if (!(key in updatedPrefs)) return; // nothing to change
      delete updatedPrefs[key];
    } else {
      const pref: ActivityVariablePref[] = activityFormData.variables.map(
        (v) => ({
          name: v.name,
          unit: v.defaultUnit,
          ...(hiddenVars.includes(v.name) ? { hidden: true } : {}),
        }),
      );
      updatedPrefs[key] = pref;
    }

    await userService.updateUserProfile(currentUser.uid, {
      activityVariablePreferences: updatedPrefs,
    });
    await refreshUserData();
  };

  const handleEditActivityType = (activityType: ActivityType) => {
    setEditingActivityType(activityType);

    // Base variables from the exercise definition
    const baseVariables = normalizeActivityVariables(
      activityType.variables || [],
    ).map((v) => ({
      name: v.name,
      measurement: null,
      unit: v.defaultUnit || getDefaultUnit(v.name, isImperial),
    }));

    // Apply the athlete's saved display preference (order / unit / hidden)
    const savedPref =
      userData?.activityVariablePreferences?.[getCustomizeKey(activityType)];
    const applied = applyPreferenceToVariables(baseVariables, savedPref);

    setActivityFormData({
      name: activityType.name,
      description: activityType.description || "",
      variables: applied.map((v) => ({
        name: v.name,
        defaultUnit: v.unit || getDefaultUnit(v.name, isImperial),
      })),
      image: activityType.image || "",
    });
    setHiddenVars(applied.filter((v) => v.hidden).map((v) => v.name));
    setShowAddActivityForm(true);
  };

  const handleDeleteActivityType = async (activityType: ActivityType) => {
    if (!currentUser?.uid || !activityType.id) return;
    try {
      setSavingActivityTypes(true);
      await activityService.deleteActivityType(
        currentUser.uid,
        activityType.id,
      );
      const typesData = await activityService.fetchActivityTypes(
        currentUser.uid,
      );
      setActivities(typesData);
      setActivityTypeToDelete(null);
      resetActivityTypeForm();
    } catch (error) {
      console.error("Error deleting activity type:", error);
      setActivityTypeError(
        error instanceof Error
          ? error.message
          : "Failed to delete exercise. Please try again.",
      );
    } finally {
      setSavingActivityTypes(false);
    }
  };

  const handleActivityCancel = () => {
    resetActivityTypeForm();
  };

  // Build the preference storage key for a given activity
  const getCustomizeKey = (activity: ActivityType): string =>
    getActivityPrefKey({
      activityTypeId: activity.id || "",
      globalCategoryId: activity.isFromPack ? activity.categoryId : undefined,
    });

  const handleActivityReset = () => {
    setActivityFormData({
      name: "",
      description: "",
      variables: [],
      image: "",
    });
    setActivityTypeError(null);
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setShowActivityResetConfirmation(false);
    setHiddenVars([]);
  };

  // Pagination helper
  const paginateArray = <T,>(
    array: T[],
    page: number,
    itemsPerPage: number,
  ) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      items: array.slice(startIndex, endIndex),
      totalPages: Math.ceil(array.length / itemsPerPage),
      currentPage: page,
      totalItems: array.length,
    };
  };

  // Filtered and paginated activities
  const getFilteredActivities = () => {
  const allActs = [...activities, ...packActivityTypes];

  const result = allActs
    .filter((activity) => {
      if (activityFilter === "custom") return !activity.isFromPack;
      if (activityFilter !== "all")
        return activity.isFromPack && activity.categoryId === activityFilter;
      return true;
    })
    .filter((activity) => {
      if (!activitiesSearchTerm) return true;
      const searchLower = activitiesSearchTerm.toLowerCase();
      return (
        activity.name.toLowerCase().includes(searchLower) ||
        activity.description?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return result;
};

  return (
    <div className="min-h-screen">
      <Header />

      {/* Main Content */}
      <div className="pt-16 pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6 pt-8">
            <div className="flex justify-between items-center w-full px-8 sm:px-0">
              <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-base-content font-heading">
                Exercises
              </h1>
              <ListAddButton
                label="New exercise"
                onClick={() => {
                  if (!canCreateActivity) {
                    return;
                  }
                  setShowAddActivityForm(true);
                }}
              />
            </div>
          </div>

          {/* Activities Search Bar and Filter */}
          <div className="mb-4 px-8 sm:px-0 space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <PeakTextField
                  label="Search"
                  value={activitiesSearchTerm}
                  onChange={(value) => {
                    setActivitiesSearchTerm(value);
                    setActivitiesPage(1);
                  }}
                  placeholder="Search exercises…"
                  autoCapitalize="none"
                />
              </div>
              <ListFilterToggle
                open={showActivityFilters}
                active={showActivityFilters || activityFilter !== "all"}
                onToggle={() => setShowActivityFilters((s) => !s)}
              />
            </div>
            {showActivityFilters && (
              <PeakSelect
                label="Show"
                value={activityFilter}
                onChange={(value) => {
                  setActivityFilter(value);
                  setActivitiesPage(1);
                }}
                emphasizedValue
                options={[
                  { value: "all", label: "All Exercises" },
                  { value: "custom", label: "Custom Exercises" },
                  ...globalCategories
                    .filter((category) =>
                      userAddedPackIds.includes(category.id || ""),
                    )
                    .map((category) => ({
                      value: category.id || "",
                      label: category.name,
                    })),
                ]}
              />
            )}
          </div>
          {/* Nudge to add activity packs if user has none */}
          {!loadingGlobalActivities &&
            userAddedPackIds.length === 0 &&
            globalCategories.length > 0 && (
              <div className="mx-8 sm:mx-0 mb-4 flex items-center justify-between gap-4 rounded-lg bg-peak-bg-secondary p-4">
                <div className="flex-1">
                  <p className="font-funnel text-[15px] font-semibold text-peak-text">
                    Add exercise packs to your library
                  </p>
                  <p className="font-funnel text-xs text-peak-text-tertiary mt-0.5">
                    Get instant access to pre-built exercises for your sport — no need
                    to create them yourself.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/settings/athlete-preferences")}
                  className="flex-shrink-0 rounded-full bg-peak-text px-4 py-2 font-funnel text-sm font-semibold text-peak-bg transition-opacity hover:opacity-90"
                >
                  Browse Packs
                </button>
              </div>
            )}

          {/* Activity List */}
          {loadingActivities ? (
            <div className="py-16 text-center font-funnel text-sm text-peak-text-tertiary">
              Loading exercises...
            </div>
          ) : activities.length === 0 && packActivityTypes.length === 0  ? (
            <div className="text-center pt-20">
              <h3 className="text-sm font-medium text-base-content/60 mb-4 font-heading">
                No exercises created yet.
              </h3>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowAddActivityForm(true)}
                  className="relative flex mx-auto justify-center items-center gap-2 px-4 py-2 border border-base-300 text-base-content rounded-lg hover:bg-base-100 transition-colors font-grotesk text-sm"
                >
                  <HiPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                  Add Exercise
                </button>
              </div>
            </div>
          ) : (
            (() => {
              const filtered = getFilteredActivities();
              const paginatedData = paginateArray(
                filtered,
                activitiesPage,
                ACTIVITIES_PER_PAGE,
              );

              if (filtered.length === 0) {
                return (
                  <div className="mx-8 sm:mx-0 py-16 text-center font-funnel text-sm text-peak-text-tertiary">
                    No exercises match your filters.
                  </div>
                );
              }

              return (
                <>
                  <div className="mx-8 sm:mx-0 border-t border-peak-border">
                    {paginatedData.items.map((activity) => {
                      const isCustomized = Boolean(
                        userData?.activityVariablePreferences?.[
                          getCustomizeKey(activity)
                        ],
                      );
                      const variableNames = (activity.variables ?? []).map(
                        (variable) =>
                          typeof variable === "string" ? variable : variable.name,
                      );
                      return (
                        <button
                          key={activity.id}
                          type="button"
                          onClick={() => setDetailActivity(activity)}
                          className={`${LIST_ROW_CLASS} block w-full text-left transition-colors hover:bg-peak-bg-secondary/50`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-funnel text-[15px] font-semibold text-peak-text">
                              {activity.name}
                            </span>
                            {isCustomized && (
                              <span
                                className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-peak-text-tertiary"
                                title="Display customized"
                              />
                            )}
                          </div>
                          <p className="mt-1 truncate font-funnel text-xs font-medium text-peak-text-tertiary">
                            {variableNames.length > 0
                              ? variableNames.join("  ·  ")
                              : "No variables"}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mx-8 sm:mx-0">
                    <ListPagination
                      currentPage={activitiesPage}
                      totalPages={paginatedData.totalPages}
                      onPageChange={setActivitiesPage}
                    />
                  </div>
                </>
              );
            })()
          )}
        </div>
      </div>

      <ExerciseDetailSheet
        activity={detailActivity}
        packName={
          detailActivity?.isFromPack
            ? globalCategories.find((c) => c.id === detailActivity.categoryId)
                ?.name
            : undefined
        }
        onClose={() => setDetailActivity(null)}
        onEdit={(activity) => {
          setDetailActivity(null);
          handleEditActivityType(activity);
        }}
      />

      {/* Add Activity Modal */}
      {showAddActivityForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4"
          onClick={handleActivityCancel}
        >
          <div
            className="bg-base-100 w-full h-full sm:rounded-xl sm:shadow-xl sm:max-w-2xl sm:w-full md:max-h-[70vh] md:min-h-[70vh] flex flex-col relative rounded-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex bg-base-100 items-center justify-between p-8 pb-0 flex-shrink-0 md:rounded-t-xl">
              <div className="flex items-center gap-2">
                <h2 className="text-xl text-base-content font-grotesk">
                  {editingActivityType
                    ? editingIsFromPack
                      ? "Customize"
                      : "Edit"
                    : "Add"}{" "}
                  <strong>Exercise</strong>
                </h2>
                {!editingActivityType && (
                  <button
                    onClick={() => setShowActivityResetConfirmation(true)}
                    disabled={savingActivityTypes}
                    className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
                    title="Reset all fields"
                  >
                    <HiArrowPath className="text-lg" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {editingActivityType && !editingIsFromPack && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivityTypeToDelete(editingActivityType);
                    }}
                    disabled={savingActivityTypes}
                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 rounded-full hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    title="Delete exercise"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleActivityCancel}
                  disabled={savingActivityTypes}
                  className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Form - Scrollable */}
            <div className="flex-1 overflow-y-auto overflow-x-visible flex flex-col">
              <form
                onSubmit={handleActivitySubmit}
                className="p-4 px-8 space-y-4 pb-0 bg-base-100"
              >
                {editingIsFromPack && (
                  <div className="flex items-start gap-2 rounded-lg border border-base-300 bg-base-200/60 p-3 text-xs text-base-content/70 font-grotesk">
                    <HiAdjustmentsHorizontal className="mt-0.5 flex-shrink-0 text-base text-base-content/50" />
                    <span>
                      This is a Library exercise. You can't change its name or
                      description, but you can set the variable order, units, and
                      hide any you don't want to see.
                    </span>
                  </div>
                )}
                <div className="new-form-group-rounded">
                  <input
                    type="text"
                    value={activityFormData.name}
                    onChange={(e) =>
                      setActivityFormData({
                        ...activityFormData,
                        name: e.target.value,
                      })
                    }
                    placeholder=" "
                    className="form-element-rounded"
                    disabled={savingActivityTypes || editingIsFromPack}
                    required
                  />
                  <label>Exercise Name</label>
                </div>
                <div className="new-form-group-rounded">
                  <textarea
                    value={activityFormData.description}
                    onChange={(e) =>
                      setActivityFormData({
                        ...activityFormData,
                        description: e.target.value,
                      })
                    }
                    placeholder=" "
                    rows={4}
                    className="form-element-rounded resize-none"
                    style={{ height: "100px" }}
                    disabled={savingActivityTypes || editingIsFromPack}
                    required
                  />
                  <label>Description</label>
                </div>
                {/* Activity Variables */}
                <div className="form-control">
                  <div className="mb-2">
                    <label className="text-sm font-bold text-base-content/80 block font-heading">
                      Variables
                    </label>
                    <p className="text-xs text-base-content/60 mb-4">
                      Choose what to track, set units, reorder, and hide any you
                      don't want to see.
                    </p>

                    {/* Selected variables — ordered, with unit / hide / remove */}
                    <div className="space-y-2">
                      {activityFormData.variables.map((variable, index) => {
                        const isPreset = AVAILABLE_VARIABLES.includes(
                          variable.name,
                        );
                        const isHidden = hiddenVars.includes(variable.name);
                        const availableUnits = getUnitsForVariable(
                          variable.name,
                          isImperial,
                        );
                        return (
                          <div
                            key={variable.name}
                            className={`flex items-center gap-2 p-2.5 rounded-lg border transition-colors ${
                              isHidden
                                ? "border-base-300 bg-base-200/40"
                                : "border-base-300 bg-base-100"
                            }`}
                          >
                            {/* Order controls */}
                            <div className="flex flex-col">
                              <button
                                type="button"
                                onClick={() => moveVariable(index, "up")}
                                disabled={index === 0 || savingActivityTypes}
                                className="p-0.5 text-base-content/40 hover:text-base-content disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                <ChevronUpIcon className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveVariable(index, "down")}
                                disabled={
                                  index ===
                                    activityFormData.variables.length - 1 ||
                                  savingActivityTypes
                                }
                                className="p-0.5 text-base-content/40 hover:text-base-content disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                <ChevronDownIcon className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Name */}
                            <div className="flex-1 min-w-0">
                              <span
                                className={`text-sm font-bold font-grotesk truncate ${
                                  isHidden
                                    ? "text-base-content/50"
                                    : "text-base-content"
                                }`}
                              >
                                {variable.name}
                              </span>
                              {!isPreset && (
                                <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                  Custom
                                </span>
                              )}
                            </div>

                            {/* Unit */}
                            <input
                              type="text"
                              list={`units-${variable.name}`}
                              value={variable.defaultUnit}
                              onChange={(e) =>
                                updateVariableUnit(
                                  variable.name,
                                  e.target.value,
                                )
                              }
                              disabled={savingActivityTypes}
                              placeholder="unit"
                              className="w-16 px-2 py-1 text-xs text-center border border-base-300 rounded bg-base-100 font-grotesk focus:outline-none focus:ring-1 focus:ring-primary/40"
                            />
                            <datalist id={`units-${variable.name}`}>
                              {availableUnits.map((unit) => (
                                <option key={unit} value={unit} />
                              ))}
                            </datalist>

                            {/* Hide toggle */}
                            <button
                              type="button"
                              onClick={() =>
                                toggleHiddenVariable(variable.name)
                              }
                              disabled={savingActivityTypes}
                              className="p-1.5 text-base-content/50 hover:text-base-content hover:bg-base-200 rounded-lg transition-colors disabled:opacity-50"
                              title={
                                isHidden
                                  ? "Hidden — click to show"
                                  : "Visible — click to hide"
                              }
                            >
                              {isHidden ? (
                                <EyeSlashIcon className="w-4 h-4" />
                              ) : (
                                <EyeIcon className="w-4 h-4" />
                              )}
                            </button>

                            {/* Remove (definition edit only) */}
                            {!editingIsFromPack && (
                              <button
                                type="button"
                                onClick={() => toggleVariable(variable.name)}
                                disabled={savingActivityTypes}
                                className="p-1.5 text-base-content/40 hover:text-red-600 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                title="Remove variable"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {activityFormData.variables.length === 0 && (
                      <p className="text-xs text-base-content/60 mt-2 font-grotesk">
                        *Please add at least one variable
                      </p>
                    )}

                    {/* Add variables (definition edit only) */}
                    {!editingIsFromPack && (
                      <div className="mt-4">
                        {AVAILABLE_VARIABLES.filter(
                          (variableName) => !isVariableSelected(variableName),
                        ).length > 0 && (
                          <>
                            <p className="text-xs text-base-content/60 mb-2 font-grotesk">
                              Add a variable
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {AVAILABLE_VARIABLES.filter(
                                (variableName) =>
                                  !isVariableSelected(variableName),
                              ).map((variableName) => (
                                <button
                                  key={variableName}
                                  type="button"
                                  onClick={() => toggleVariable(variableName)}
                                  disabled={savingActivityTypes}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-base-content/80 bg-base-200 border border-base-300 rounded-full hover:bg-base-300 transition-colors disabled:opacity-50"
                                >
                                  <PlusIcon className="w-3 h-3" />
                                  {variableName}
                                </button>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Add Custom Variable */}
                        {!showCustomVariableInput ? (
                          <button
                            type="button"
                            onClick={() => setShowCustomVariableInput(true)}
                            disabled={savingActivityTypes}
                            className="flex items-center space-x-2 p-2 rounded-lg border-2 border-dashed border-base-300 hover:border-base-content/50 hover:bg-base-200 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg
                              className="w-4 h-4 text-base-content/50"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                            <span className="text-sm text-base-content/70 font-grotesk">
                              Add custom variable
                            </span>
                          </button>
                        ) : (
                          <div
                            ref={customVariableInputRef}
                            className="p-3 border border-base-300 rounded-lg bg-base-200 space-y-2"
                          >
                            <div>
                              <label className="text-xs text-base-content/80 font-grotesk mb-1 block font-medium">
                                Variable Name
                              </label>
                              <input
                                type="text"
                                value={customVariableName}
                                onChange={(e) =>
                                  setCustomVariableName(e.target.value)
                                }
                                placeholder="e.g., Cadence, Power, Elevation"
                                className="w-full px-3 py-2 text-sm border bg-base-200 border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-grotesk"
                                disabled={savingActivityTypes}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" &&
                                    customVariableName.trim()
                                  ) {
                                    e.preventDefault();
                                    addCustomVariable();
                                  } else if (e.key === "Escape") {
                                    cancelCustomVariable();
                                  }
                                }}
                                autoFocus
                              />
                            </div>
                            <div>
                              <label className="text-xs text-base-content/80 font-grotesk mb-1 block font-medium">
                                Default Unit
                              </label>
                              <input
                                type="text"
                                value={customVariableUnit}
                                onChange={(e) =>
                                  setCustomVariableUnit(e.target.value)
                                }
                                placeholder="e.g., bpm, watts, m"
                                className="w-full px-3 py-2 text-sm border bg-base-200 border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-grotesk"
                                disabled={savingActivityTypes}
                              />
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                type="button"
                                onClick={addCustomVariable}
                                disabled={
                                  savingActivityTypes ||
                                  !customVariableName.trim()
                                }
                                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium"
                              >
                                Add Variable
                              </button>
                              <button
                                type="button"
                                onClick={cancelCustomVariable}
                                disabled={savingActivityTypes}
                                className="px-4 py-2 text-sm text-base-content/80 bg-base-100 border border-base-300 rounded hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {activityTypeError && (
                  <div className="text-red-600 text-sm sm:text-base bg-red-500/10 border border-red-200 rounded-lg p-3 font-grotesk">
                    {activityTypeError}
                  </div>
                )}
              </form>
              <div className="pb-20 px-8 mt-auto mb-12` pt-10">
                <div className="flex items-center overflow-hidden">
                  <button
                    type="button"
                    onClick={handleActivitySubmit}
                    disabled={savingActivityTypes}
                    className="bg-primary text-white rounded-full py-3 px-6 font-grotesk font-bold disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
                  >
                    {savingActivityTypes ? (
                      <>
                        <span className="loading loading-spinner loading-sm mr-2"></span>
                        {editingActivityType ? "Saving..." : "Adding..."}
                      </>
                    ) : editingIsFromPack ? (
                      "Save Preferences"
                    ) : editingActivityType ? (
                      "Update Exercise"
                    ) : (
                      "Add Exercise"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Reset Confirmation Dialog */}
      <Transition appear show={showActivityResetConfirmation} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setShowActivityResetConfirmation(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-base-100 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-base-content font-grotesk"
                  >
                    Reset All Fields?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-base-content/60 font-sans">
                      Are you sure you want to reset all fields? This will clear
                      the exercise name, description, and variables. This action
                      cannot be undone.
                    </p>
                  </div>
                  <div className="mt-4 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-base-300 bg-base-100 px-4 py-2 text-sm font-medium text-base-content/80 hover:bg-base-200 focus:outline-none font-grotesk"
                      onClick={() => setShowActivityResetConfirmation(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none font-grotesk"
                      onClick={handleActivityReset}
                    >
                      Reset
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Activity Type Delete Confirmation Dialog */}
      {activityTypeToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setActivityTypeToDelete(null)}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-base-content mb-4 font-heading">
              Delete Exercise
            </h3>
            <p className="text-base-content/70 mb-6 font-grotesk">
              Are you sure you want to delete the exercise{" "}
              <strong>"{activityTypeToDelete.name}"</strong>? This will hide it
              from future use, but existing sessions and measurements will
              remain intact.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setActivityTypeToDelete(null)}
                disabled={savingActivityTypes}
                className="px-4 py-3 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteActivityType(activityTypeToDelete)}
                disabled={savingActivityTypes}
                className="px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                {savingActivityTypes ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Deleting...
                  </>
                ) : (
                  "Delete Exercise"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavBar />
    </div>
  );
};

/**
 * Read-only summary of an exercise: where it comes from, its description and
 * the variables it records. Custom exercises can be edited from here; pack
 * exercises open the display customiser.
 */
const ExerciseDetailSheet: React.FC<{
  activity: ActivityType | null;
  packName?: string;
  onClose: () => void;
  onEdit: (activity: ActivityType) => void;
}> = ({ activity, packName, onClose, onEdit }) => {
  const source = activity?.isFromPack
    ? packName
      ? `${packName} pack`
      : "Exercise pack"
    : "Custom exercise";
  const description = activity?.description?.trim();
  const variables = (activity?.variables ?? []).map((variable) => {
    if (typeof variable === "string") return { name: variable, unit: "—" };
    const units =
      variable.unitsList && variable.unitsList.length > 1
        ? variable.unitsList.join(" / ")
        : variable.defaultUnit?.trim();
    return { name: variable.name, unit: units || "—" };
  });

  return (
    <BottomSheet
      isOpen={!!activity}
      onClose={onClose}
      title={activity?.name ?? ""}
      subtitle={source}
      footer={
        activity && (
          <button
            type="button"
            onClick={() => onEdit(activity)}
            className="h-12 w-full rounded-full border border-peak-border font-funnel text-[15px] font-semibold text-peak-text transition-colors hover:bg-peak-bg-secondary"
          >
            {activity.isFromPack ? "Customise display" : "Edit exercise"}
          </button>
        )
      }
    >
      {description && (
        <p className="font-funnel text-[15px] text-peak-text-secondary whitespace-pre-wrap">
          {description}
        </p>
      )}
      <div>
        <p className="mb-1 font-funnel text-[13px] font-semibold text-peak-text-tertiary">
          Variables
        </p>
        {variables.length === 0 ? (
          <p className="py-3 font-funnel text-[15px] text-peak-text-tertiary">
            No variables
          </p>
        ) : (
          variables.map((v) => (
            <div
              key={v.name}
              className="flex items-baseline justify-between gap-3 border-b border-peak-border py-3"
            >
              <span className="font-funnel text-[15px] font-medium text-peak-text">
                {v.name}
              </span>
              <span className="font-geist text-[13px] font-medium text-peak-text-tertiary">
                {v.unit}
              </span>
            </div>
          ))
        )}
      </div>
    </BottomSheet>
  );
};

export default Activities;
